import React from 'react';
import {
  PDFViewer, Page, Text, View, Document, StyleSheet, Font, Image, Svg, Line,
} from '@react-pdf/renderer';
import { useLocation } from 'react-router';
import OutlinedPersonIcon from './PdfIcons/OutlinedPersonIcon';
import FilledPersonIcon from './PdfIcons/FilledPersonIcon';

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/fonts/Roboto-Regular.ttf`,
    },
    {
      src: `${process.env.PUBLIC_URL}/fonts/Roboto-Bold.ttf`,
      fontWeight: 'bold',
    },
    {
      src: `${process.env.PUBLIC_URL}/fonts/Roboto-Italic.ttf`,
      fontStyle: 'italic',
    },
    {
      src: `${process.env.PUBLIC_URL}/fonts/Roboto-BoldItalic.ttf`,
      fontWeight: 'bold',
      fontStyle: 'italic',
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  body: {
    flexGrow: 1,
    fontFamily: 'Roboto',
    fontSize: 12,
    margin: '40px',
  },
  primaryRow: {
    width: '100%',
    flexDirection: 'row',
    padding: '20px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  secondaryRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  secondaryRowLeftJustify: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingBottom: '10px',
  },
  textBlock: {
    flexDirection: 'column',
    paddingBottom: '4px',
  },
  header: {
    fontSize: '18px',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '100%',
  },
  subheader: {
    fontSize: '15px',
    fontWeight: 'bold',
    paddingBottom: '8px',
  },
  bodyTextBold: {
    fontWeight: 'bold',
  },
  bodyTextUnderlined: {
    textDecoration: 'underline',
  },
  statusBox: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    flexGrow: 0,
    width: '90%',
    paddingBottom: '22px',
  },
  prediction: {
    padding: '15px',
    border: '2px solid black',
    borderRadius: '20px',
    margin: '0px 20px 18px 20px',
    width: '100%',
  },
  secondaryPrediction: {
    padding: '15px',
    border: '2px solid black',
    borderRadius: '20px',
    marginBottom: '26px',
    margin: '0px 20px 0px 20px',
    width: '100%',
    backgroundColor: '#F3F5F9',
  },
  logoImage: {
    width: '100px',
  },
});

interface DocumentProps {
  prediction: { primary: number, secondary: number | null };
}
const MyDocument = (props: DocumentProps) : JSX.Element => {
  const { prediction } = props;
  return (
    <Document>
      <Page size="LETTER">
        <View style={styles.body}>
          <View style={styles.secondaryRow}>
            <Image style={styles.logoImage} src={`${process.env.PUBLIC_URL}/images/sinai-logo.png`} />
          </View>
          <View style={styles.secondaryRow}>
            <Svg height="30" width="490">
              <Line
                x1="0"
                y1="20"
                x2="490"
                y2="20"
                strokeWidth={1}
                stroke="rgb(164,164,164)"
              />
            </Svg>
          </View>
          <View style={styles.primaryRow}>
            <Text style={styles.header}>
              Personalized Risk Assessment
              {'\n'}
              for Hysterectomy Procedure
            </Text>
          </View>
          <View style={[styles.primaryRow, { paddingTop: '0px' }]}>
            <Text style={{ paddingBottom: '12px', textAlign: 'justify' }}>
              {'All operations have a small risk of complications. For some people, these risks are '}
              {'higher. Certain complications are more serious and, in surgery, complications are sometimes '}
              {'given a Grade based on a scale called the '}
              <Text style={styles.bodyTextBold}>Clavien-Dindo Scale</Text>
              .
            </Text>
            <Text style={{ paddingBottom: '12px', textAlign: 'justify' }}>
              {'Grade 1 complications are usually mild but Grade 2 and higher complications are more significant. Examples include '}
              {'(but are not limited to): infections requiring antibiotics, blood transfusions or injury to '}
              abdominal or pelvic structures.
            </Text>
            <Text style={{ textAlign: 'justify', fontWeight: 'bold' }}>
              {'Based on several factors related to your health and surgery, your risk of a Grade 2 '}
              or higher complication has been calculated.
            </Text>
          </View>
          <View style={styles.secondaryRow}>
            <View style={styles.prediction}>
              <View style={styles.secondaryRowLeftJustify}>
                <Text style={styles.bodyTextBold}>
                  Your personalized risk assessment is:
                </Text>
              </View>
              <View style={styles.secondaryRow}>
                <View style={styles.textBlock}>
                  <Text>
                    <Text style={styles.bodyTextBold}>
                      {`${prediction.primary} out of 100 `}
                    </Text>
                    have a Grade 2 or higher complication,
                    <Text style={styles.bodyTextBold}>
                      {` ${100 - prediction.primary} do not`}
                    </Text>
                    .
                  </Text>
                </View>
              </View>
              <View style={styles.secondaryRow}>
                <View style={styles.statusBox}>
                  {Array.from({ length: 100 }, (_, i) => {
                    if (i <= prediction.primary) {
                      return (
                        <FilledPersonIcon key={`icon-${i}`} />
                      );
                    }
                    return (
                      <OutlinedPersonIcon key={`icon-${i}`} />
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
          {prediction.secondary && (
            <View style={styles.secondaryRow}>
              <View style={styles.secondaryPrediction}>
                <View style={styles.secondaryRowLeftJustify}>
                  <Text>
                    <Text style={{ fontWeight: 'bold' }}>
                      You are currently anemic.
                    </Text>
                    <Text>
                      {' If we can correct your anemia prior to your surgery, '}
                      your risk of a Grade 2 or higher complication becomes:
                    </Text>
                  </Text>
                </View>
                <View style={styles.secondaryRow}>
                  <View style={styles.textBlock}>
                    <Text>
                      <Text style={styles.bodyTextBold}>
                        {`${prediction.secondary} out of 100 `}
                      </Text>
                      have a Grade 2 or higher complication,
                      <Text style={styles.bodyTextBold}>
                        {` ${100 - prediction.secondary} do not`}
                      </Text>
                      .
                    </Text>
                  </View>
                </View>
                <View style={styles.secondaryRow}>
                  <View style={styles.statusBox}>
                    {Array.from({ length: 100 }, (_, i) => {
                      if (prediction.secondary !== null && i <= prediction.secondary) {
                        return (
                          <FilledPersonIcon key={`icon-${i}`} />
                        );
                      }
                      return (
                        <OutlinedPersonIcon key={`icon-${i}`} />
                      );
                    })}
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

interface PrintOutProps {
  prediction?: { primary: number, secondary: number | null };
}
function PrintOut() : JSX.Element {
  const location = useLocation();
  const { prediction } = (location.state as PrintOutProps);
  return (
    <div style={{ height: '100vh', display: 'flex' }}>
      <PDFViewer style={{ flex: 'auto' }}>
        <MyDocument prediction={prediction || { primary: 0, secondary: null }} />
      </PDFViewer>
    </div>
  );
}

export default PrintOut;
